
import katex from 'katex';
import {$applyNodeReplacement, DecoratorNode} from 'lexical';
import * as React from 'react';
import EquationComponent from './EquationComponent';


function $convertEquationElement(domNode) {
  let equation = domNode.getAttribute('data-lexical-equation');
  const inline = domNode.getAttribute('data-lexical-inline') === 'true';

  if (equation) {
    // Case 1: base64 attribute is present
    equation = atob(equation);
  } else {
    // Case 2: no base64 → extract from rendered KaTeX
    const katexEl = domNode.querySelector('.katex');
    if (katexEl) {
      // Using textContent so we get raw LaTeX (if available)
      // If you rendered KaTeX with trust=false, it won't store LaTeX in DOM,
      // so you might need to store original LaTeX in a data attribute when exporting.
      equation = katexEl.textContent.trim();
    }
  }

  if (equation) {
    const node = $createEquationNode(equation, inline);
    return { node };
  }

  return null;
}

export class EquationNode extends DecoratorNode {
  __equation;
  __inline;

  static getType() {
    return 'equation';
  }

  static clone(node) {
    return new EquationNode(node.__equation, node.__inline, node.__key);
  }

  constructor(equation, inline, key) {
    super(key);
    this.__equation = equation;
    this.__inline = inline ?? false;
  }

  static importJSON(serializedNode) {
    return $createEquationNode(
      serializedNode.equation,
      serializedNode.inline,
    ).updateFromJSON(serializedNode);
  }

  exportJSON() {
    return {
      ...super.exportJSON(),
      equation: this.getEquation(),
      inline: this.__inline,
    };
  }

  createDOM(_config) {
    const element = document.createElement(this.__inline ? 'span' : 'div');
    // EquationNodes should implement `user-action:none` in their CSS to avoid issues with deletion on Android.
    element.className = 'editor-equation';
    return element;
  }

exportDOM() {
  const element = document.createElement(this.__inline ? 'span' : 'div');
  const equationBase64 = btoa(this.__equation);
  element.setAttribute('data-lexical-equation', equationBase64);
  element.setAttribute('data-lexical-inline', `${this.__inline}`);
  element.classList.add('editor-equation');

  katex.render(this.__equation, element, {
    displayMode: !this.__inline,
    errorColor: '#cc0000',
    output: 'html',
    strict: 'warn',
    throwOnError: false,
    trust: false,
  });

  return { element };
}


  static importDOM() {
  return {
    span: (domNode) => {
      const isEquation =
        domNode.classList.contains('editor-equation') ||
        domNode.getAttribute('data-lexical-decorator') === 'true';

      if (!isEquation) {
        return null;
      }

      return {
        conversion: $convertEquationElement,
        priority: 2, // make sure this overrides normal span import
      };
    },
    div: (domNode) => {
      const isEquation =
        domNode.classList.contains('editor-equation') ||
        domNode.getAttribute('data-lexical-decorator') === 'true';

      if (!isEquation) {
        return null;
      }

      return {
        conversion: $convertEquationElement,
        priority: 2,
      };
    },
  };
}


  updateDOM(prevNode) {
    // If the inline property changes, replace the element
    return this.__inline !== prevNode.__inline;
  }

  getTextContent() {
    return this.__equation;
  }

  getEquation() {
    return this.__equation;
  }

  setEquation(equation){
    const writable = this.getWritable();
    writable.__equation = equation;
  }

  decorate(){
    return (
      <EquationComponent
        equation={this.__equation}
        inline={this.__inline}
        nodeKey={this.__key}
      />
    );
  }
}

export function $createEquationNode(
  equation = '',
  inline = false,
) {
  const equationNode = new EquationNode(equation, inline);
  return $applyNodeReplacement(equationNode);
}

export function $isEquationNode(
  node,
) {
  return node instanceof EquationNode;
}